import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Banner, Button, Card, Dialog, Money, Pill } from '@elysian/ui';
import type { BankAccountSummary, BankFeedConnection, FeedInstitution } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { shortDate } from '../invoices/shared';

const STATUS: Record<BankFeedConnection['status'], { tone: 'ok' | 'warn' | 'danger' | 'draft'; label: string }> = {
  active: { tone: 'ok', label: 'Connected' },
  expired: { tone: 'warn', label: 'Consent ended' },
  revoked: { tone: 'warn', label: 'Revoked at the bank' },
  error: { tone: 'danger', label: 'Not fetching' },
};

/**
 * Open-banking feeds (Enable Banking): connect AIB, Bank of Ireland or
 * Revolut, choose which ledger bank account each shared account feeds, and
 * fetch now. New lines arrive on the reconciliation screen; nothing posts.
 */
export function BankFeeds({ accounts, onSynced }: { accounts: BankAccountSummary[]; onSynced: () => void }) {
  const { source, can } = useSession();
  const toast = useToast((s) => s.show);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [version, setVersion] = useState(0);
  const data = useAsync(() => source.bankFeeds(), [source, version], { keep: true });
  const [connecting, setConnecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const may = can('bank.reconcile');

  // Back from the bank's consent page.
  const outcome = params.get('feed');
  useEffect(() => {
    if (!outcome) return;
    const message = params.get('message');
    if (outcome === 'connected') toast(message ?? 'Bank connected');
    else if (outcome === 'cancelled') toast('Bank connection cancelled');
    else setError(message ?? 'The bank didn’t give consent.');
    setParams({}, { replace: true });
    setVersion((v) => v + 1);
  }, [outcome, params, setParams, toast]);

  async function run<T>(work: () => Promise<T>, done?: (r: T) => void) {
    setBusy(true);
    setError(null);
    try {
      const r = await work();
      done?.(r);
      setVersion((v) => v + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function connect(institution: string) {
    await run(() => source.connectBankFeed(institution, 'IE'), ({ authorise_url }) => {
      setConnecting(false);
      if (authorise_url.startsWith('/')) navigate(authorise_url);
      else window.location.assign(authorise_url);
    });
  }

  const sync = () => run(() => source.syncBankFeeds(), ({ results }) => {
    const added = results.reduce((n, r) => n + (r.added ?? 0), 0);
    const failed = results.find((r) => r.error);
    if (failed) setError(failed.error!);
    toast(added ? `${added} new bank line${added === 1 ? '' : 's'} to reconcile` : 'No new bank lines');
    onSynced();
  });

  const connections = data.state === 'ready' ? data.data.connections : [];
  const anyLinked = connections.some((c) => c.accounts.some((a) => a.account_code));

  return (
    <Card padding="20px">
      <div className="el-row" style={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 className="el-h2" style={{ margin: 0 }}>Bank feeds</h2>
          <p className="el-hint" style={{ margin: '4px 0 0' }}>
            Transactions come in from the bank every few hours and wait on the reconciliation screen. Read-only: the app can’t move money.
          </p>
        </div>
        {may && data.state === 'ready' && data.data.configured && (
          <div className="el-row">
            {anyLinked && <Button variant="ghost" disabled={busy} onClick={() => void sync()}>Sync now</Button>}
            <Button variant="primary" disabled={busy} onClick={() => setConnecting(true)}>Connect a bank</Button>
          </div>
        )}
      </div>
      {error && <div className="el-error-box" role="alert" style={{ marginTop: 12 }}>{error}</div>}
      {data.state === 'error' && <Banner>Couldn’t load bank feeds: {data.error.message}</Banner>}
      {data.state === 'ready' && !data.data.configured && (
        <p className="el-muted">Bank feeds aren’t set up on this server yet. Statement files can still be imported on the reconciliation screen.</p>
      )}
      {data.state === 'ready' && data.data.configured && connections.length === 0 && (
        <p className="el-muted">No bank connected. Connect AIB, Bank of Ireland or Revolut to stop downloading statement files.</p>
      )}
      {connections.map((c) => (
        <Connection key={c.id} connection={c} accounts={accounts} may={may} busy={busy}
          onLink={(id, code) => run(() => source.linkBankFeedAccount(id, code), () => toast(code ? `Feeding ${code}` : 'Unlinked'))}
          onRenew={() => void connect(c.institution)}
          onDisconnect={() => run(() => source.disconnectBankFeed(c.id), () => toast(`${c.institution} disconnected`))} />
      ))}
      {connecting && <ConnectDialog busy={busy} onPick={(name) => void connect(name)} onClose={() => setConnecting(false)} />}
    </Card>
  );
}

function Connection({ connection: c, accounts, may, busy, onLink, onRenew, onDisconnect }: {
  connection: BankFeedConnection; accounts: BankAccountSummary[]; may: boolean; busy: boolean;
  onLink: (id: string, code: string | null) => void; onRenew: () => void; onDisconnect: () => void;
}) {
  const status = STATUS[c.status];
  const ended = c.status === 'expired' || c.status === 'revoked';
  return (
    <section aria-label={`${c.institution} feed`} style={{ marginTop: 16 }}>
      <div className="el-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <strong>{c.institution}</strong> <Pill tone={status.tone}>{status.label}</Pill>
          {c.renew_soon && c.status === 'active' && <Pill tone="warn">Renew by {shortDate(c.consent_expires_at.slice(0, 10))}</Pill>}
          <div className="el-hint">
            {ended ? 'The bank stopped sharing. Renew to carry on from the last line fetched.'
              : `Consent until ${shortDate(c.consent_expires_at.slice(0, 10))}`}
            {c.last_error && c.status === 'error' ? ` · ${c.last_error}` : ''}
          </div>
        </div>
        {may && (
          <div className="el-row">
            {(ended || c.renew_soon) && <Button size="sm" variant="primary" disabled={busy} onClick={onRenew}>Renew</Button>}
            <Button size="sm" variant="ghost" disabled={busy} onClick={onDisconnect} aria-label={`Disconnect ${c.institution}`}>Disconnect</Button>
          </div>
        )}
      </div>
      <div className="el-table-wrap">
        <table className="el-table" aria-label={`${c.institution} accounts`}>
          <thead>
            <tr><th scope="col">At the bank</th><th scope="col">Feeds</th><th scope="col" className="el-num">Bank balance</th><th scope="col">Last fetched</th></tr>
          </thead>
          <tbody>
            {c.accounts.map((a) => {
              const choices = accounts.filter((l) => l.currency === a.currency);
              return (
                <tr key={a.id}>
                  <td><strong>{a.name ?? 'Account'}</strong><div className="el-hint">{a.iban_masked ?? ''} · {a.currency}</div></td>
                  <td>
                    <select className="el-input" style={{ minWidth: 170 }} aria-label={`Ledger account fed by ${c.institution} ${a.name ?? ''}`.trim()} disabled={!may || busy}
                      value={a.account_code ?? ''} onChange={(e) => onLink(a.id, e.target.value || null)}>
                      <option value="">Not used</option>
                      {choices.map((l) => <option key={l.code} value={l.code}>{l.code} {l.name}</option>)}
                    </select>
                    {choices.length === 0 && <div className="el-hint">No {a.currency} bank account in the books yet</div>}
                  </td>
                  <td className="el-num">
                    {a.last_balance != null ? <Money value={String(a.last_balance)} currency={a.currency} /> : '—'}
                    {a.last_balance_date && <div className="el-hint">on {shortDate(a.last_balance_date)}</div>}
                  </td>
                  <td>
                    {a.last_synced_at ? shortDate(a.last_synced_at.slice(0, 10)) : a.account_code ? 'Waiting for the first fetch' : '—'}
                    {a.last_error && <div className="el-hint">{a.last_error}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ConnectDialog({ busy, onPick, onClose }: { busy: boolean; onPick: (name: string) => void; onClose: () => void }) {
  const { source } = useSession();
  const banks = useAsync(() => source.feedInstitutions('IE'), [source]);
  const [showAll, setShowAll] = useState(false);
  const list: FeedInstitution[] = banks.state === 'ready' ? banks.data : [];
  const shown = showAll ? list : list.filter((b) => b.preferred);
  return (
    <Dialog title="Connect a bank" onClose={onClose} actions={<Button variant="ghost" onClick={onClose}>Cancel</Button>}>
      <p className="el-hint" style={{ margin: 0 }}>
        You’ll sign in at the bank and choose which accounts to share. Banks ask again every 90 to 180 days; the app reminds you a week before.
      </p>
      {banks.state === 'loading' && <div className="el-skeleton" style={{ height: 18 }} aria-label="Loading banks" />}
      {banks.state === 'error' && <div className="el-error-box" role="alert">{banks.error.message}</div>}
      <div className="el-row" role="list" aria-label="Banks" style={{ flexWrap: 'wrap' }}>
        {shown.map((b) => (
          <div role="listitem" key={b.name}>
            <Button disabled={busy} onClick={() => onPick(b.name)}>{b.name}</Button>
          </div>
        ))}
      </div>
      {!showAll && list.length > shown.length && <Button size="sm" variant="ghost" onClick={() => setShowAll(true)}>Other banks…</Button>}
    </Dialog>
  );
}
